import type {
  DealSubmissionDocumentDto,
  DealSubmissionDocumentRequestDto,
  DealSubmissionEvaluationFreshnessStatus,
  DealSubmissionSectionDto,
  DealSubmissionSectionStateDto,
} from "../../api/types";

export function evaluationStatusLabel(status: DealSubmissionEvaluationFreshnessStatus): string {
  switch (status) {
    case "Current": return "Evaluated ✓";
    case "Stale": return "Re-evaluate needed";
    case "NotEvaluated": return "Not evaluated";
    case "NotApplicable": return "Not evaluated";
  }
}

export function evaluationStatusTone(status: DealSubmissionEvaluationFreshnessStatus): string {
  switch (status) {
    case "Current": return "text-status-success";
    case "Stale": return "text-status-error";
    case "NotEvaluated":
    case "NotApplicable":
      return "text-gray-mid";
  }
}

// The feedback-enabled documents in a section that are uploaded but not yet
// current. Section feedback depends on these, so they gate the section-eval
// button (mirrors the server-side guard in RunSectionFeedbackBySectionAsync).
export function outstandingDocumentEvaluationNames(
  documentRequests: DealSubmissionDocumentRequestDto[],
  documents: DealSubmissionDocumentDto[],
): string[] {
  const names: string[] = [];
  for (const request of documentRequests) {
    if (!request.feedbackEnabled) continue;
    const doc = documents.find((d) => d.documentRequestId === request.id);
    if (!doc) continue;
    if (doc.aiFeedbackStatus === "NotEvaluated") names.push(request.label);
    if (doc.aiFeedbackStatus === "Stale") names.push(`${request.label} (re-evaluate needed)`);
  }
  return names;
}

export function outstandingEvaluationNames(
  section: DealSubmissionSectionDto,
  sectionState: DealSubmissionSectionStateDto | null,
  documentRequests: DealSubmissionDocumentRequestDto[],
  documents: DealSubmissionDocumentDto[],
): string[] {
  const names = outstandingDocumentEvaluationNames(documentRequests, documents);

  if (section.feedbackEnabled) {
    const status = sectionState?.aiFeedbackStatus ?? "NotEvaluated";
    if (status === "NotEvaluated") names.push("this section");
    if (status === "Stale") names.push("this section (re-evaluate needed)");
  }

  return names;
}

export function formatOutstandingEvaluationMessage(names: string[]): string | null {
  if (names.length === 0) return null;
  return `Run Evaluate & Feedback on: ${formatList(names)} before continuing.`;
}

// Reason shown when the section eval is blocked on its documents — names them
// and explains why section feedback depends on the document reviews.
export function formatSectionPrerequisiteMessage(names: string[]): string | null {
  if (names.length === 0) return null;
  return `Run Evaluate & Feedback on ${formatList(names)} first — section feedback builds on the document reviews.`;
}

function formatList(items: string[]): string {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
